<template>
  <div class="cohort-ui">
    <div class="flex min-h-screen bg-gray-100">
      <nav
        :class="[
          'fixed flex h-full flex-col bg-white shadow-xl transition-all duration-300',
          isNavCollapsed ? 'w-20' : 'w-64'
        ]"
        :aria-label="L.cohort_navigation_aria"
      >
        <div class="flex items-center px-4 py-4" :class="isNavCollapsed ? 'justify-center' : 'justify-between'">
          <img :src="isNavCollapsed ? logoCompact : logoMain" :class="isNavCollapsed ? 'h-8 w-8' : 'h-8 w-auto'" alt="SleepReport" />
        </div>

        <div v-if="!isNavCollapsed" class="mx-3 mt-2 rounded-lg border border-blue-100 bg-blue-50 p-3">
          <div class="text-[10px] text-gray-400">{{ L.cohort_current_queue }}</div>
          <div class="mt-1 truncate text-xs font-semibold text-gray-700">{{ currentCohort.name }}</div>
          <button class="mt-2 text-xs font-medium text-blue-600 hover:text-blue-700" @click="router.push('/cohorts')">{{ L.cohort_switch_queue }}</button>
        </div>
        <button v-else class="mx-auto mt-2 flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 text-blue-600" :title="L.cohort_switch_queue" :aria-label="L.cohort_switch_queue" @click="router.push('/cohorts')">
          <ChartBarSquareIcon class="h-5 w-5" />
        </button>

        <div v-if="canReturnToOriginal" class="mx-3 mt-3">
          <button class="flex w-full items-center rounded-lg border border-gray-200 bg-white py-2.5 text-sm font-medium text-gray-600 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600" :class="isNavCollapsed ? 'justify-center px-2' : 'px-3'" :title="isNavCollapsed ? L.cohort_return_original : L.cohort_return_sleepreport_original" :aria-label="L.cohort_return_sleepreport_original" @click="router.push('/devices')">
            <ArrowUturnLeftIcon class="h-5 w-5 shrink-0" />
            <span v-if="!isNavCollapsed" class="ml-2">{{ L.cohort_return_original }}</span>
          </button>
        </div>

        <div class="flex-1 overflow-y-auto overflow-x-hidden px-2 py-4">
          <section v-for="(group, groupIndex) in menuGroups" :key="group.key" class="cohort-menu-group" :class="groupIndex ? 'mt-5 border-t border-gray-100 pt-4' : ''">
            <h2 v-if="!isNavCollapsed" class="px-4 pb-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-400">{{ group.label }}</h2>
            <div class="space-y-1">
              <router-link
                v-for="item in group.items"
                :key="item.path"
                :to="item.path"
                :title="isNavCollapsed ? item.name : ''"
                :aria-label="item.name"
                :aria-current="isMenuItemActive(item) ? 'page' : undefined"
                class="group relative flex items-center rounded-lg py-3 text-sm font-medium transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
                :class="[
                  isMenuItemActive(item) ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                  isNavCollapsed ? 'justify-center px-2' : 'px-4'
                ]"
              >
                <span v-if="isMenuItemActive(item)" class="absolute inset-y-2 left-0 w-0.5 rounded-full bg-blue-500" aria-hidden="true"></span>
                <component :is="item.icon" class="h-6 w-6 shrink-0 transition-transform duration-150 group-hover:scale-105" :class="isMenuItemActive(item) ? 'text-blue-600' : 'text-gray-400'" />
                <span v-if="!isNavCollapsed" class="ml-3 truncate">{{ item.name }}</span>
              </router-link>
            </div>
          </section>
        </div>

        <div class="border-t border-gray-200 px-4 py-3">
          <div class="flex items-center justify-between">
            <div v-if="!isNavCollapsed" class="truncate text-sm text-gray-600" style="max-width: 140px">
              {{ user?.username || user?.phoneNumber || t('unknown_user') }}
            </div>
            <button class="flex items-center text-sm text-gray-600 transition-colors duration-150 hover:text-gray-900" :class="{ 'mx-auto': isNavCollapsed }" :aria-label="t('logout')" @click="logout">
              <ArrowLeftOnRectangleIcon class="h-4 w-4" :class="{ 'mr-1': !isNavCollapsed }" />
              <span v-if="!isNavCollapsed">{{ t('logout') }}</span>
            </button>
          </div>
        </div>

        <button class="absolute -right-3 top-1/2 -translate-y-1/2 rounded-full bg-white p-1 shadow-md hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300" :aria-label="isNavCollapsed ? L.cohort_expand_navigation : L.cohort_collapse_navigation" @click="isNavCollapsed = !isNavCollapsed">
          <ChevronLeftIcon class="h-4 w-4 text-gray-600" :class="{ 'rotate-180': isNavCollapsed }" />
        </button>
      </nav>

      <main class="min-w-0 flex-1 transition-all duration-300" :class="isNavCollapsed ? 'ml-20' : 'ml-64'">
        <div class="p-3 sm:p-4 md:p-6">
          <router-view />
        </div>
        <SleepReportAssistant v-if="!isResearchRestricted" />
      </main>
    </div>
  </div>
</template>

<script setup>
import { isResearchRestricted } from '@/modules/cohort/researchPermissions.js'

import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import {
  ArrowLeftOnRectangleIcon,
  ArrowUturnLeftIcon,
  ChartBarSquareIcon,
  ChevronLeftIcon
} from '@heroicons/vue/24/outline'
import logo from '@/assets/logo.png'
import logoCompact from '@/assets/logo_1.png'
import logoEn from '@/assets/logo-en.svg'
import { currentLocale, L, t } from '@/plugins/utils/i18n'
import SleepReportAssistant from '@/modules/cohort/components/SleepReportAssistant.vue'
import { selectedCohortState } from '@/modules/cohort/cohortSelection.js'
import { createCohortMenuItems } from '@/modules/cohort/navigation.js'
import { refreshCohortSelection } from '@/modules/cohort/cohortDiscovery.js'
import '@/modules/cohort/styles/cohort.css'

const router = useRouter()
const route = useRoute()
const store = useStore()
const isNavCollapsed = ref(typeof window !== 'undefined' && window.innerWidth < 768)
const user = computed(() => store.state.user)
const canReturnToOriginal = computed(() => !isResearchRestricted.value && user.value?.sleepreport_display_mode !== 'cohort')
const logoMain = computed(() => currentLocale.value === 'en' ? logoEn : logo)
const currentCohort = computed(() => selectedCohortState.value || { name: L.cohort_select_queue_prompt })
const menuGroups = computed(() => createCohortMenuItems(L, selectedCohortState.value))

onMounted(async () => {
  try {
    const selected = await refreshCohortSelection()
    if (!selected) await router.replace('/cohorts')
    else if (route.path === '/cohort/realtime' && selected.realtime_monitoring_enabled !== true) {
      await router.replace('/cohort')
    }
  } catch (error) {
    console.warn('队列权限刷新失败', error)
  }
})

function isMenuItemActive(item) {
  return route.path === item.path || (item.path !== '/cohort' && route.path.startsWith(`${item.path}/`))
}

function logout() {
  store.commit('clearUser')
  localStorage.clear()
  router.push('/login')
}
</script>
